import { PropsWithChildren, useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import classNames from "classnames";
import { useFeatureFlagEnabled } from "posthog-js/react";
import ProviderUserForm, { ProviderUserFormValues } from "./ProviderUserForm";
import { gql } from "../../__generated__";
import { ContinueButton } from "../buttons/ContinueButton";
import Loader from "../shared/Loader";
import { PageError } from "../PageError";
import InlineBanner from "../shared/InlineBanner";
import ProviderBAAForm from "../ProviderBAAForm/ProviderBAAForm";

import styles from "./user-provider-setup-wrapper.module.scss";

export const CREATE_PROVIDER = gql(`
  mutation CreateProvider($input: CreateProviderViaSelfSignUpInput!){
    createProviderViaSelfSignUp(input: $input) {
      providerId
      hasSignedBAA
    }
  }
`);

export const UPDATE_PROVIDER_USER_INFO = gql(`
    mutation UpdateProviderUserInfo($input: UpdateProviderUserInfoInput!) {
      updateProviderUserInfo(input: $input) {
        providerUserId
      }
    }
`);

export const MARK_BAA_SIGNED = gql(`
    mutation SignBAA {
      signProviderBusinessAssociateAgreement {
        providerId
        hasSignedBAA
      }
    }
`);

export const GET_PROVIDER = gql(`
  query ProviderQuery {
    provider {
      providerId
      hasSignedBAA
    }
  }
`);

export const GET_PROVIDER_USER = gql(`
  query ProviderUserQuery {
    providerUser {
      providerUserId
      firstName
      lastName
    }
  }
`);

export function UserProviderSetupWrapper({ children }: PropsWithChildren) {
  const [isCreateMutationsLoading, setIsCreateMutationsLoading] =
    useState(false);
  const [providerBAASigned, setProviderBAASigned] = useState(false);
  const [hasErrorOnSubmit, setHasErrorOnSubmit] = useState(false);
  const {
    data: providerUserData,
    loading: providerUserLoading,
    error: providerUserError,
  } = useQuery(GET_PROVIDER_USER);
  const {
    data: providerData,
    loading: providerLoading,
    error: providerError,
  } = useQuery(GET_PROVIDER);

  const [createProvider, { loading: createProviderLoading }] = useMutation(
    CREATE_PROVIDER,
    {
      onError: () => {
        setHasErrorOnSubmit(true);
      },
    },
  );

  const [signBAA] = useMutation(MARK_BAA_SIGNED, {
    onError: () => {
      setHasErrorOnSubmit(true);
    },
  });

  const [updateProviderUserInfo, { loading: createProviderUserLoading }] =
    useMutation(UPDATE_PROVIDER_USER_INFO, {
      onError: () => {
        setHasErrorOnSubmit(true);
      },
    });

  useEffect(() => {
    setIsCreateMutationsLoading(
      createProviderLoading || createProviderUserLoading,
    );
  }, [createProviderLoading, createProviderUserLoading]);

  const handleSubmit = async (formData: ProviderUserFormValues) => {
    setIsCreateMutationsLoading(true);
    setHasErrorOnSubmit(false);
    const { data: providerResponse } = await createProvider({
      variables: {
        input: {
          providerName: formData.practiceName as string,
          identificationNumber: formData.physicianNPI as string,
          specialty: formData.specialty as string,
        },
      },
    });

    if (!providerResponse) {
      setIsCreateMutationsLoading(false);

      throw new Error("Failed to create provider");
    }

    await updateProviderUserInfo({
      variables: {
        input: {
          firstName: formData.firstName as string,
          lastName: formData.lastName as string,
        },
      },
      refetchQueries: [{ query: GET_PROVIDER_USER }, { query: GET_PROVIDER }],
    });
  };

  const handleSignBAA = async () => {
    setHasErrorOnSubmit(false);

    if (!providerData?.provider?.providerId) {
      setHasErrorOnSubmit(true);

      throw new Error(
        "No provider ID to sign BAA. This should not be possible.",
      );
    }

    await signBAA({
      refetchQueries: [{ query: GET_PROVIDER }],
    });
  };

  if (providerLoading || providerUserLoading) {
    return (
      <div className={classNames(styles.loadingWrapper)}>
        <Loader color="loader_almost_black" height="48" width="48" />
      </div>
    );
  }

  if (providerError || providerUserError) {
    return <PageError />;
  }

  if (!providerUserData?.providerUser && !providerData?.provider) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.pageContainer}>
          {hasErrorOnSubmit && (
            <InlineBanner
              message={
                <span className={styles.errorText}>
                  {"We ran into an issue. Try again or "}
                  <a
                    className={styles.supportLink}
                    href="mailto://support@getclaimable.com"
                  >
                    {"Contact Support"}
                  </a>
                </span>
              }
              type="error"
              dismissable
              onDismiss={() => setHasErrorOnSubmit(false)}
              className={styles.errorMessage}
            />
          )}
          <h2 className={styles.subheading}>
            {"Almost there! Set up your account"}
          </h2>
          <h1 className={styles.heading}>
            {"Tell us some basic information about you"}
          </h1>
          <ProviderUserForm formId="providerInfoForm" onSubmit={handleSubmit} />
          <ContinueButton
            type="submit"
            form="providerInfoForm"
            className={styles.continueButton}
            isLoading={isCreateMutationsLoading}
            disabled={isCreateMutationsLoading}
          />
        </div>
      </div>
    );
  }

  if (providerData?.provider && !providerData?.provider.hasSignedBAA) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.agreementPageContainer}>
          {hasErrorOnSubmit && (
            <InlineBanner
              message={
                <span className={styles.errorText}>
                  {"We ran into an issue. Try again or "}
                  <a
                    className={styles.supportLink}
                    href="mailto://support@getclaimable.com"
                  >
                    {"Contact Support"}
                  </a>
                </span>
              }
              type="error"
              dismissable
              onDismiss={() => setHasErrorOnSubmit(false)}
              className={styles.errorMessage}
            />
          )}
          <h2 className={styles.subheading}>{"Let's make it official"}</h2>
          <h1 className={styles.heading}>
            {"Agree to BAA and service agreement"}
          </h1>
          <ProviderBAAForm
            onCheckboxClicked={(event) =>
              setProviderBAASigned(event.target.checked)
            }
          />
          <ContinueButton
            disabled={!providerBAASigned}
            onClick={handleSignBAA}
            className={styles.continueButton}
          >
            {"Agree and Continue"}
          </ContinueButton>
        </div>
      </div>
    );
  }

  return children;
}

export function UserProviderSetupWrapperWithFeatureFlag({
  children,
}: PropsWithChildren) {
  const featureEnabled = useFeatureFlagEnabled("self-serve-provider-signup");

  if (featureEnabled) {
    return <UserProviderSetupWrapper>{children}</UserProviderSetupWrapper>;
  }

  return children;
}
